import React, { useState, useEffect } from 'react';
import { Search, Sparkles, BrainCircuit } from 'lucide-react';

/**
 * TypingIndicator — Shimmer skeleton bubble with phase-aware status text.
 *
 * Shows a message-like ghost bubble with shimmer lines and a status label
 * that reflects what the assistant is doing (thinking, searching, generating).
 * Includes fade-in on mount via CSS animation.
 *
 * Phase-specific skeletons:
 * - SEARCHING → source card skeletons (horizontal chips)
 * - PROCESSING → text line skeletons (wider, multi-line)
 * - CLASSIFYING / default → compact shimmer lines
 */

interface TypingIndicatorProps {
  isVisible: boolean;
  size?: 'sm' | 'md';
  tone?: 'dark' | 'light';
  className?: string;
  statusText?: string | null;
  statusCode?: string | null;
}

/** Pick icon based on status phase */
function PhaseIcon({ status }: { status?: string | null }) {
  const cls = 'w-3.5 h-3.5 flex-shrink-0 animate-pulse';
  if (
    status?.includes('documentos') ||
    status?.includes('Revisando') ||
    status?.includes('Buscando en')
  )
    return <Search className={cls} />;
  if (
    status?.includes('respuesta') ||
    status?.includes('Preparando') ||
    status?.includes('Generando') ||
    status?.includes('Analizando:') ||
    status?.includes('Analisis inteligente')
  )
    return <Sparkles className={cls} />;
  return <BrainCircuit className={cls} />;
}

/** Shimmer gradient shared across skeleton types */
const shimmerStyle = {
  backgroundImage:
    'linear-gradient(90deg, rgb(var(--nb-surface-hover-rgb) / 0.4) 25%, rgb(var(--nb-surface-hover-rgb) / 0.8) 50%, rgb(var(--nb-surface-hover-rgb) / 0.4) 75%)',
  backgroundSize: '200% 100%',
};

/** SEARCHING phase — 3 source-card ghost chips */
function SearchingSkeleton() {
  const widths = [140, 110, 160];
  return (
    <div className="flex gap-2 mt-2">
      {widths.map((w, i) => (
        <div
          key={i}
          className="h-12 rounded-lg animate-shimmer border border-nb-border/40 flex-shrink-0"
          style={{
            width: `${w}px`,
            ...shimmerStyle,
            animationDelay: `${i * 150}ms`,
          }}
        />
      ))}
    </div>
  );
}

/** PROCESSING phase — 3 text-line skeletons of varying width */
function ProcessingSkeleton() {
  const widths = [85, 95, 70];
  return (
    <div className="space-y-2 mt-2 w-full max-w-[380px]">
      {widths.map((pct, i) => (
        <div
          key={i}
          className="h-3 rounded-full animate-shimmer"
          style={{
            width: `${pct}%`,
            ...shimmerStyle,
            animationDelay: `${i * 100}ms`,
          }}
        />
      ))}
    </div>
  );
}

/** Default shimmer lines (CLASSIFYING / unknown phase) */
function DefaultSkeleton() {
  return (
    <div className="space-y-1.5">
      <div className="h-3 rounded-full animate-shimmer" style={{ width: '85%', ...shimmerStyle }} />
      <div
        className="h-3 rounded-full animate-shimmer"
        style={{ width: '60%', animationDelay: '0.2s', ...shimmerStyle }}
      />
      <div
        className="h-3 rounded-full animate-shimmer"
        style={{ width: '40%', animationDelay: '0.4s', ...shimmerStyle }}
      />
    </div>
  );
}

export function TypingIndicator({
  isVisible,
  className = '',
  statusText,
  statusCode,
}: TypingIndicatorProps) {
  // Keep component mounted briefly after hiding for fade-out (I3)
  const [mounted, setMounted] = useState(false);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setMounted(true);
      setFading(false);
    } else if (mounted) {
      setFading(true);
      const timer = setTimeout(() => setMounted(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isVisible, mounted]);

  if (!mounted) return null;

  // Detect phase — prefer explicit code, fall back to text matching
  const isSearching = statusCode === 'SEARCHING' || statusText?.toLowerCase().includes('revisando');
  const isProcessing =
    statusCode === 'PROCESSING' || statusText?.toLowerCase().includes('preparando');

  // Truncate long "Buscando en: ..." lists to first 2 names + count
  let displayText = statusText || 'Pensando...';
  const buscandoMatch = displayText.match(/^(Buscando en|Analisis inteligente de): (.+)$/);
  if (buscandoMatch) {
    const names = buscandoMatch[2].replace(/\.\.\.$/, '').split(', ');
    if (names.length > 2) {
      displayText = `${buscandoMatch[1]}: ${names[0]}, ${names[1]} y ${names.length - 2} más...`;
    }
  }

  return (
    <div
      className={`flex justify-start items-start gap-2 mb-2 transition-opacity duration-200 ${
        fading ? 'opacity-0' : 'opacity-100 animate-fade-in'
      } ${className}`}
    >
      {/* Sparkle icon aligned with message sparkles */}
      <Sparkles className="w-5 h-5 text-nb-accent flex-shrink-0 mt-[14px] animate-pulse" />

      {/* Shimmer skeleton bubble */}
      <div className="max-w-[75%] min-w-[200px]">
        <div className="px-4 py-3 rounded-2xl space-y-2">
          {/* Status label with phase icon */}
          <div className="flex items-center gap-2 text-xs text-nb-text-secondary">
            <PhaseIcon status={statusText} />
            <span className="animate-fade-in">{displayText}</span>
          </div>

          {/* Phase-specific skeleton */}
          {isSearching ? (
            <SearchingSkeleton />
          ) : isProcessing ? (
            <ProcessingSkeleton />
          ) : (
            <DefaultSkeleton />
          )}
        </div>
      </div>
    </div>
  );
}
